// Knowledge base page format — ontwerp §5.
//
// A page is Markdown with an optional YAML frontmatter block:
//
//   ---
//   title: API-routes
//   summary: Routes dispatch to services
//   applies_to: [packages/api/src/routes/**]
//   tags: [api]
//   always_load: false
//   verified_at: 1746ab4
//   ---
//   # API-routes
//   …
//
// Parsing the YAML itself is left to the API (js-yaml) so this package stays
// dependency-free; what lives here is the pure split/join of the block and
// the schema for the parsed object. Unknown fields are kept (passthrough).

import { z } from 'zod'
import { KB_GLOB_MAX_LENGTH, kbGlobError } from './glob.js'
import { KB_SUMMARY_MAX_LENGTH } from './limits.js'
import { kbTitleFromPath } from './path.js'

// ---------------------------------------------------------------------------
// Split / join
// ---------------------------------------------------------------------------

export interface KbFrontmatterSplit {
  /** The YAML between the fences (without them), or `null` when the text has no frontmatter block. */
  frontmatter: string | null
  /** Everything after the closing fence (one leading newline dropped). */
  body: string
}

const OPEN_RE = /^﻿?---[ \t]*\r?\n/
const CLOSE_RE = /^(?:---|\.\.\.)[ \t]*$/

/**
 * Splits a page's full text into its frontmatter block and body. A block
 * only counts when the text *starts* with a `---` line and a closing `---`
 * (or `...`) line follows; otherwise the whole text is body.
 */
export function splitKbFrontmatter(raw: string): KbFrontmatterSplit {
  const open = OPEN_RE.exec(raw)
  if (!open) return { frontmatter: null, body: raw }
  const rest = raw.slice(open[0].length)
  const lines = rest.split('\n')
  let offset = 0
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!.replace(/\r$/, '')
    if (CLOSE_RE.test(line)) {
      const yaml = rest.slice(0, offset).replace(/\r?\n$/, '')
      let body = rest.slice(offset + lines[i]!.length)
      body = body.replace(/^\r?\n/, '')
      // A blank line between the fence and the content is formatting, not content.
      body = body.replace(/^\r?\n/, '')
      return { frontmatter: yaml, body }
    }
    offset += lines[i]!.length + 1
  }
  return { frontmatter: null, body: raw }
}

/** Inverse of `splitKbFrontmatter`: `yaml` empty/null → just the body. */
export function joinKbFrontmatter(yaml: string | null | undefined, body: string): string {
  const trimmed = (yaml ?? '').replace(/\s+$/, '')
  if (trimmed === '') return body
  return `---\n${trimmed}\n---\n\n${body.replace(/^\n+/, '')}`
}

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const toStringArray = (value: unknown) => (typeof value === 'string' ? [value] : value)

/** `verified_at` may come out of YAML as a number (all-digit sha) or a Date. */
const toVerifiedString = (value: unknown) => {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'number') return String(value)
  return value
}

/** One `applies_to` glob; the message says why an invalid one doesn't compile. */
export const KbAppliesToPatternSchema = z
  .string()
  .trim()
  .min(1)
  .max(KB_GLOB_MAX_LENGTH)
  .superRefine((pattern, ctx) => {
    const error = kbGlobError(pattern)
    if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error })
  })

/** Validates a whole `applies_to` list (same rules as the frontmatter schema); `null` when valid. */
export function kbAppliesToError(patterns: readonly string[]): string | null {
  for (const pattern of patterns) {
    const error = kbGlobError(pattern)
    if (error) return error
  }
  return null
}

export const KbFrontmatterSchema = z
  .object({
    title: z.string().trim().min(1).max(200).optional(),
    /** ≤ 300 characters is the target; longer text is cut by `truncateKbSummary`. */
    summary: z.string().trim().max(2000).optional(),
    /**
     * Globs relative to the repository root (`**`, `*`, `?`, `[…]`, `{a,b}`) — see `kb/glob.ts`.
     * Each must compile: ≤ 300 characters, ≤ 32 `{}` expansions, no nested braces, valid ranges.
     */
    applies_to: z.preprocess(toStringArray, z.array(KbAppliesToPatternSchema).max(50)).optional(),
    tags: z.preprocess(toStringArray, z.array(z.string().trim().min(1).max(50)).max(30)).optional(),
    /** Humans only; see the API's always_load budget check. */
    always_load: z.boolean().optional(),
    /** Commit (or date) the page was last verified against. */
    verified_at: z.preprocess(toVerifiedString, z.string().trim().min(1).max(100)).optional(),
    /** Set by a delete; a deleted page is hidden but restorable for 30 days. */
    deleted: z.boolean().optional(),
  })
  .passthrough()

export type KbFrontmatter = z.infer<typeof KbFrontmatterSchema>

/** Known frontmatter keys, in the order the API writes them back. */
export const KB_FRONTMATTER_KEYS = ['title', 'summary', 'applies_to', 'tags', 'always_load', 'verified_at', 'deleted'] as const

// ---------------------------------------------------------------------------
// Derivation (title / summary fallbacks)
// ---------------------------------------------------------------------------

/** First `# ` heading in the body (outside code fences), or `null`. */
export function firstKbHeading(body: string): string | null {
  let inFence = false
  for (const line of body.split('\n')) {
    if (/^\s*(```|~~~)/.test(line)) inFence = !inFence
    if (inFence) continue
    const match = /^#\s+(.+?)\s*#*\s*$/.exec(line)
    if (match) return match[1]!.trim()
  }
  return null
}

/** First paragraph of the body that is not a heading, fence, table, rule or HTML comment. */
export function firstKbParagraph(body: string): string | null {
  const lines = body.split('\n')
  const paragraph: string[] = []
  let inFence = false
  for (const raw of lines) {
    const line = raw.trim()
    if (/^(```|~~~)/.test(line)) {
      inFence = !inFence
      if (paragraph.length) break
      continue
    }
    if (inFence) continue
    if (line === '') {
      if (paragraph.length) break
      continue
    }
    if (/^(#{1,6}\s|---+$|\*\*\*+$|\||<!--)/.test(line)) {
      if (paragraph.length) break
      continue
    }
    paragraph.push(line.replace(/^[-*+]\s+|^>\s?/, ''))
  }
  const text = paragraph.join(' ').replace(/\s+/g, ' ').trim()
  return text === '' ? null : text
}

/** Cuts to `KB_SUMMARY_MAX_LENGTH` characters, ending in '…' when cut. */
export function truncateKbSummary(text: string, max = KB_SUMMARY_MAX_LENGTH): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (clean.length <= max) return clean
  return `${clean.slice(0, max - 1).trimEnd()}…`
}

/** §5: frontmatter title → first `#` heading → file name. */
export function deriveKbTitle(frontmatter: Pick<KbFrontmatter, 'title'>, body: string, path: string): string {
  return frontmatter.title?.trim() || firstKbHeading(body) || kbTitleFromPath(path)
}

/** §5: frontmatter summary → first paragraph, both cut to 300 characters. `null` when neither exists. */
export function deriveKbSummary(frontmatter: Pick<KbFrontmatter, 'summary'>, body: string): string | null {
  const source = frontmatter.summary?.trim() || firstKbParagraph(body)
  return source ? truncateKbSummary(source) : null
}
